<script setup lang="ts">
import { computed } from "vue";
import { VerticalBarChart } from "@/features/Charts";
import type { WasteMaterial, Emission } from "@/api/services/waste/types";
import { useTooltip } from "@stores/useTooltip";
import format from "@/services/Utils/format";
import type { ChartTooltipEventType } from "@/features/Charts";

const props = defineProps<{
  material: WasteMaterial;
  emission: Emission;
  wasteMappingUnit?: string;
  projectUnits?: { originalEmission: string; scaledEmission: string };
  weight?: { original: number; scaled: number };
  totalQuantity?: { unit: string; value: number } | null;
}>();

const tooltip = useTooltip();
const unit = computed(() => props.projectUnits?.scaledEmission ?? props.wasteMappingUnit ?? "kg CO₂e");

const chartData = computed(() => ({
  datapoint: {
    name: "A5",
    entries: [
      {
        label: "A5",
        value: props.emission.scaled,
      },
    ],
  },
}));

const formatTooltipText = (event: ChartTooltipEventType) => {
  return `
        <div class="flex flex-col text-white">
          <span class="t-caption-bold">${event?.name}</span>
          <span class="t-caption">${format.decimalNumber(event?.value as number)} ${unit.value}</span>
        </div>
      `;
};

const onHover = (event: ChartTooltipEventType) => {
  tooltip.showTooltip(formatTooltipText(event));
};

const onLeave = () => {
  tooltip.hideTooltip();
};
</script>

<template>
  <div class="flex flex-col">
    <h4 class="t-h4 text-type-default pb-4">{{ material.name }}</h4>

    <div class="flex gap-6">
      <!-- Left: Unit / Value table -->
      <div class="text-text-color t-small flex-1">
        <div class="border-element-stroke t-small-bold grid grid-cols-2 rounded-t-xl border bg-slate-100 p-2">
          <span class="pl-2">Unit</span>
          <span>Value</span>
        </div>
        <div
          class="border-element-stroke grid grid-cols-2 items-center border-x border-b px-4 py-2"
          :class="{ 'rounded-b-xl': !totalQuantity }">
          <span>kg</span>
          <span>{{ weight != null ? format.decimalNumber(weight.original, 2) : "-" }}</span>
        </div>
        <div
          v-if="totalQuantity"
          class="border-element-stroke grid grid-cols-2 items-center rounded-b-xl border-x border-b px-4 py-2">
          <span>{{ totalQuantity.unit }}</span>
          <span>{{ format.decimalNumber(totalQuantity.value, 2) }}</span>
        </div>
      </div>

      <!-- Right: Chart -->
      <div class="flex flex-col items-center">
        <span class="text-text-color t-sh4 text-center">GWP Hotspot</span>
        <span class="t-caption">{{ unit }}</span>

        <div class="h-[460px] w-[420px]">
          <VerticalBarChart
            :data="chartData"
            :unit="unit"
            :on-hover="onHover"
            :on-leave="onLeave"
            :settings="{ rotateLabels: 90 }" />
        </div>
      </div>
    </div>
  </div>
</template>
